import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Box, Button, Chip, Container, Divider,
  Grid, MenuItem, Select, Stack, TextField, Typography, CircularProgress,
} from '@mui/material';
const ExpandMoreIcon = () => <svg style={{ width: 24, height: 24, flexShrink: 0 }} viewBox="0 0 24 24"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="currentColor"/></svg>;

function makeAuthHeader(creds) {
  if (!creds?.user || !creds?.pass) return {};
  return { Authorization: `Basic ${btoa(`${creds.user}:${creds.pass}`)}` };
}

function timeAgo(dtStr) {
  if (!dtStr) return '\u2014';
  const diff = Date.now() - new Date(dtStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function DevicePage() {
  const { wfmc } = useParams();
  const navigate = useNavigate();

  const [expanded, setExpanded]   = useState('overview');
  const [device, setDevice]       = useState(null);
  const [readings, setReadings]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [form, setForm]           = useState(null);
  const [busy, setBusy]           = useState(false);
  const [msg, setMsg]             = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [adminCreds, setAdminCreds] = useState(null);
  const [targetPpm, setTargetPpm] = useState('450');
  const [abcHours, setAbcHours]   = useState('180');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    fetch('/api/access/projects')
      .then(r => r.json())
      .then(j => { if (j?.ok) setProjects(j.projects || []); })
      .catch(() => {});
  }, []);

  const ensureCreds = useCallback(() => {
    if (adminCreds?.user && adminCreds?.pass) return adminCreds;
    const user = window.prompt('Admin username:', 'admin');
    if (user == null) return null;
    const pass = window.prompt('Admin password:', 'admin');
    if (pass == null) return null;
    const next = { user, pass };
    setAdminCreds(next);
    return next;
  }, [adminCreds]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await fetch(`/api/admin/device/${wfmc}`);
      const j = await r.json();
      if (j?.ok) {
        setDevice(j.device);
        setForm({
          project:    j.device.project    || '',
          site:       j.device.site       || '',
          location:   j.device.location   || '',
          position:   j.device.position   || '',
          frq_m:            j.device.frq_m            ?? 90,
          samples:          j.device.samples          ?? 16,
          iir_filter:       j.device.iir_filter       ?? 2,
          temp_offset:      j.device.temp_offset      ?? 0,
          humidity_offset:  j.device.humidity_offset  ?? 0,
          pressure_offset:  j.device.pressure_offset  ?? 0,
        });
      }
    } catch {
      setMsg('Failed to load device');
    } finally {
      setLoading(false);
    }
  }, [wfmc]);

  useEffect(() => { load(); }, [load]);

  const loadReadings = useCallback(() => {
    fetch(`/api/admin/device/${wfmc}/readings`)
      .then(r => r.json())
      .then(j => { if (j?.ok) setReadings(j.readings || []); })
      .catch(() => {});
  }, [wfmc]);

  useEffect(() => { loadReadings(); }, [loadReadings]);

  const onSave = useCallback(async () => {
    const creds = ensureCreds();
    if (!creds) return;
    setBusy(true); setMsg('');
    try {
      const r = await fetch('/api/admin/device/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...makeAuthHeader(creds) },
        body: JSON.stringify({ wfmc, ...form }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) {
        if (r.status === 401) { setAdminCreds(null); throw new Error('Unauthorized'); }
        throw new Error(j?.message || 'Save failed');
      }
      setMsg('Saved');
      await load();
    } catch (e) {
      setMsg(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [ensureCreds, form, load, wfmc]);

  const sendAction = useCallback(async (action, extra = {}) => {
    const creds = ensureCreds();
    if (!creds) return;
    setBusy(true); setActionMsg('');
    try {
      const r = await fetch(`/api/admin/device/${wfmc}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...makeAuthHeader(creds) },
        body: JSON.stringify({ action, ...extra }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) {
        if (r.status === 401) { setAdminCreds(null); throw new Error('Unauthorized'); }
        throw new Error(j?.message || 'Failed');
      }
      setActionMsg(`Queued: ${action} \u2014 device will execute on next poll (within ${device?.frq_m ?? 90}s)`);
      await load();
    } catch (e) {
      setActionMsg(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [ensureCreds, wfmc, device, load]);

  const getGpsLocation = useCallback(() => {
    if (!navigator.geolocation) { setMsg('Geolocation not available'); return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = `${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`;
        setForm((f) => ({ ...f, location: coords }));
        setGpsLoading(false);
      },
      () => { setMsg('Could not get location'); setGpsLoading(false); }
    );
  }, []);

  const lr = device?.last_read;

  // Filter out non-sensor reading types and collect distinct hw_type values
  const sensorReadingTypes = new Set(['co2', 'ch4', 'temp', 'humidity', 'pressure', 'altitude']);
  const sensorReadings = readings.filter(r => sensorReadingTypes.has(r.type?.toLowerCase()));
  const hwTypes = [...new Set(sensorReadings.map(r => r.hw_type).filter(v => v && isNaN(Number(v))))];

  if (loading) return (
    <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
      <CircularProgress />
    </Container>
  );

  return (
    <>
      <Helmet><title>Device {wfmc} | TERRRA</title></Helmet>
      <Container maxWidth="md">
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <Button variant="text" size="small" onClick={() => navigate('/admin/dashboard')}>&larr; Dashboard</Button>
          <Typography variant="h5" sx={{ flex: 1 }}>{wfmc}</Typography>
        </Stack>

        <Box sx={{ mt: 1 }}>
          {[
            {
              id: 'overview',
              label: 'Device Overview',
              chips: [
                lr && <Chip key="time" label={timeAgo(lr.dt)} size="small" />,
                lr?.ver && <Chip key="fw" label={`fw v${lr.ver}`} size="small" color="info" />,
                device?.status && device.status !== 'NA' && <Chip key="pending" label={`pending: ${device.status}`} size="small" color="warning" />,
              ].filter(Boolean),
              content: (
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Identity</Typography>
                    <InfoRow label="MAC" value={wfmc} />
                    <InfoRow label="Router MAC" value={lr?.rwmc || '\u2014'} />
                    <InfoRow label="Hardware" value={hwTypes.length > 0 ? hwTypes.join(', ') : (lr?.hw_type || '\u2014')} />
                    <InfoRow label="Firmware" value={lr?.ver ? `v${lr.ver}` : '\u2014'} />
                    <InfoRow label="Last OTA" value={device?.last_ota_version ? `v${device.last_ota_version}${device.last_ota_at ? ` \u00b7 ${new Date(device.last_ota_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}` : '\u2014'} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Connection</Typography>
                    <InfoRow label="Signal" value={lr?.wfsg ? `${lr.wfsg} dBm` : '\u2014'} />
                    <InfoRow label="Local IP" value={lr?.local_ip || '\u2014'} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, mt: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Sensors</Typography>
                    {(() => {
                      const ds = device?.device_status;
                      const parsed = ds ? (typeof ds === 'string' ? JSON.parse(ds) : ds) : null;
                      const sensors = parsed?.sensors ? parsed.sensors.join(', ') : (hwTypes.length > 0 ? hwTypes.join(', ') : '\u2014');
                      const hasError = parsed?.error_status && parsed.error_status !== '0x0' && parsed.error_status !== '0x00';
                      return <>
                        <InfoRow label="Detected" value={sensors} />
                        <InfoRow label="Status" value={hasError ? parsed.error_status : (parsed ? 'OK' : 'Unknown')} />
                        {parsed?.uptime_s != null && <InfoRow label="Uptime" value={`${Math.floor(parsed.uptime_s / 3600)}h ${Math.floor((parsed.uptime_s % 3600) / 60)}m`} />}
                        {parsed?.free_heap != null && <InfoRow label="Free Heap" value={`${Math.round(parsed.free_heap / 1024)} KB`} />}
                      </>;
                    })()}
                  </Grid>
                </Grid>
              ),
            },
            {
              id: 'readings',
              label: 'Current Readings',
              chips: sensorReadings.length ? [<Chip key="n" label={`${sensorReadings.length} type${sensorReadings.length > 1 ? 's' : ''}`} size="small" />] : [],
              content: (
                <Stack spacing={0.5}>
                  {sensorReadings.length === 0
                    ? <Typography variant="caption" color="text.disabled">No readings in the last hour</Typography>
                    : sensorReadings.map(r => (
                      <Stack key={r.type} direction="row" alignItems="baseline" spacing={1.5}
                        sx={{ py: 0.5, borderBottom: '1px solid rgba(145,158,171,0.12)' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, textTransform: 'capitalize' }}>{r.type}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 80 }}>
                          {r.val != null ? `${r.val} ${r.units || ''}`.trim() : '\u2014'}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {r.dt ? timeAgo(r.dt) : ''}
                        </Typography>
                      </Stack>
                    ))
                  }
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" variant="outlined" onClick={loadReadings}>Refresh</Button>
                  </Box>
                </Stack>
              ),
            },
            {
              id: 'identity',
              label: 'Identity',
              content: (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Select fullWidth size="small" displayEmpty
                        value={form?.project || ''}
                        onChange={(e) => setForm((f) => ({ ...f, project: e.target.value, site: '' }))}>
                        <MenuItem value=""><em>Select project...</em></MenuItem>
                        {projects.map(p => <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>)}
                      </Select>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Select fullWidth size="small" displayEmpty
                        value={form?.site || ''}
                        onChange={(e) => setForm((f) => ({ ...f, site: e.target.value }))}
                        disabled={!form?.project}>
                        <MenuItem value=""><em>{form?.project ? 'Select site...' : 'Select project first'}</em></MenuItem>
                        {(projects.find(p => p.name === form?.project)?.sites || []).map(s =>
                          <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
                        )}
                      </Select>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <TextField fullWidth size="small" label="Location (lat, lng)" value={form?.location || ''}
                          onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                          placeholder="e.g. 32.7940, 35.5316" />
                        <Button size="small" variant="outlined" onClick={getGpsLocation} disabled={gpsLoading} sx={{ whiteSpace: 'nowrap', mt: '1px' }}>
                          {gpsLoading ? '...' : 'GPS'}
                        </Button>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Position" value={form?.position || ''}
                        onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
                        placeholder="e.g. Surface / 30cm" />
                    </Grid>
                  </Grid>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                    <Button variant="contained" size="small" onClick={onSave} disabled={busy}>Save</Button>
                    {msg && <Typography variant="body2" color={msg.toLowerCase().includes('err') || msg.toLowerCase().includes('fail') ? 'error' : 'text.secondary'}>{msg}</Typography>}
                  </Stack>
                </>
              ),
            },
            {
              id: 'settings',
              label: 'Device Settings',
              content: (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    General ESP32 settings. Pushed to the device on its next settings poll.
                  </Typography>
                  <TextField size="small" label="Measurement interval (s)" type="number"
                    value={form?.frq_m ?? 90}
                    onChange={(e) => setForm((f) => ({ ...f, frq_m: e.target.value }))}
                    helperText="How often the device reads sensors & polls for settings"
                    sx={{ maxWidth: 300 }} />
                  <Typography variant="body2" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>BME280 Calibration Offsets</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Correct for sensor-to-sensor variation. Measure next to a reference instrument, enter (reference &minus; device) for each value. Temperature offset also improves humidity accuracy.
                  </Typography>
                  <Grid container spacing={2} sx={{ maxWidth: 480 }}>
                    <Grid item xs={4}>
                      <TextField fullWidth size="small" label="Temp offset (°C)" type="number"
                        value={form?.temp_offset ?? 0}
                        onChange={(e) => setForm((f) => ({ ...f, temp_offset: e.target.value }))}
                        inputProps={{ step: 0.1 }}
                        helperText="e.g. -2.3" />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField fullWidth size="small" label="Humidity offset (%)" type="number"
                        value={form?.humidity_offset ?? 0}
                        onChange={(e) => setForm((f) => ({ ...f, humidity_offset: e.target.value }))}
                        inputProps={{ step: 0.5 }}
                        helperText="e.g. +1.5" />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField fullWidth size="small" label="Pressure offset (Pa)" type="number"
                        value={form?.pressure_offset ?? 0}
                        onChange={(e) => setForm((f) => ({ ...f, pressure_offset: e.target.value }))}
                        inputProps={{ step: 10 }}
                        helperText="e.g. -150" />
                    </Grid>
                  </Grid>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                    <Button variant="contained" size="small" onClick={onSave} disabled={busy}>Save</Button>
                    {msg && <Typography variant="body2" color={msg.toLowerCase().includes('err') || msg.toLowerCase().includes('fail') ? 'error' : 'text.secondary'}>{msg}</Typography>}
                  </Stack>
                </>
              ),
            },
            {
              id: 'sensor',
              label: 'Sensor Control (Sunrise)',
              chips: device?.param_1 || device?.status !== 'NA' ? [<Chip key="p" label="command pending" size="small" color="warning" />] : [],
              content: (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Sunrise CO2 sensor configuration and calibration. Commands are queued and executed on the next poll (within {device?.frq_m ?? 90}s).
                    {device?.param_1 && <><br /><b>Pending: {device.param_1}</b></>}
                  </Typography>
                  <Stack spacing={2.5}>
                    {(() => {
                      const ds = device?.device_status;
                      if (!ds) return (
                        <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.disabled">No sensor status reported yet. Click "Request Status" below or wait for next device boot (requires v1.2.17+).</Typography>
                          <Button size="small" onClick={load} sx={{ ml: 1, whiteSpace: 'nowrap' }}>Refresh</Button>
                        </Box>
                      );
                      const parsed = typeof ds === 'string' ? JSON.parse(ds) : ds;
                      return (
                        <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Sensor Status {device?.updated_at && <Typography component="span" variant="caption" color="text.disabled"> (reported {timeAgo(device.updated_at)})</Typography>}
                            </Typography>
                            <Button size="small" onClick={load}>Refresh</Button>
                          </Stack>
                          <Grid container spacing={1}>
                            <Grid item xs={6} sm={3}><InfoRow label="ABC" value={parsed.abc_enabled ? `On (${parsed.abc_period_h}h)` : 'Off'} /></Grid>
                            <Grid item xs={6} sm={3}><InfoRow label="Error" value={parsed.error_status === '0x0' || parsed.error_status === '0x00' || !parsed.error_status ? 'None' : parsed.error_status} /></Grid>
                            <Grid item xs={6} sm={3}><InfoRow label="Calibration" value={parsed.calib_status === '0x00' || parsed.calib_status === '0x0' || !parsed.calib_status ? 'Idle' : parsed.calib_status} /></Grid>
                            <Grid item xs={6} sm={3}><InfoRow label="Samples / IIR" value={`${parsed.num_samples ?? '\u2014'} / ${parsed.iir_filter ?? '\u2014'}`} /></Grid>
                            <Grid item xs={6} sm={3}><InfoRow label="Pressure Comp" value={parsed.pressure_comp && parsed.pressure_comp < 20000 ? `${(parsed.pressure_comp / 10).toFixed(0)} hPa` : '\u2014'} /></Grid>
                            <Grid item xs={6} sm={3}><InfoRow label="Mode" value={parsed.meas_mode || '\u2014'} /></Grid>
                            {parsed.last_cmd && <Grid item xs={6} sm={6}><InfoRow label="Last Command" value={`${parsed.last_cmd} \u2192 ${parsed.last_cmd_result || 'pending'}`} /></Grid>}
                            {parsed.diag && <Grid item xs={12}><InfoRow label="Write Diag" value={parsed.diag} /></Grid>}
                          </Grid>
                        </Box>
                      );
                    })()}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>Measurement Config</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        These values are set by the sensor hardware and cannot be changed via Modbus on this device.
                      </Typography>
                      {(() => {
                        const ds = device?.device_status;
                        const parsed = ds ? (typeof ds === 'string' ? JSON.parse(ds) : ds) : null;
                        return (
                          <Stack direction="row" spacing={3}>
                            <Box><Typography variant="caption" color="text.secondary">Samples</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{parsed?.num_samples ?? '\u2014'}</Typography></Box>
                            <Box><Typography variant="caption" color="text.secondary">IIR Filter</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{parsed?.iir_filter ?? '\u2014'}</Typography></Box>
                            <Box><Typography variant="caption" color="text.secondary">Meas Period</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{parsed?.meas_period_s ? `${parsed.meas_period_s}s` : '\u2014'}</Typography></Box>
                          </Stack>
                        );
                      })()}
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>Background Calibration</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Calibrate against fresh outdoor air (~420 ppm). Place the device outside for 10+ minutes before running.
                      </Typography>
                      <Button size="small" variant="outlined" disabled={busy} onClick={() => sendAction('backcalib')}>Run Background Calibration</Button>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>Target Calibration</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Calibrate against a known reference gas concentration.
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField size="small" label="Target ppm" type="number" value={targetPpm}
                          onChange={(e) => setTargetPpm(e.target.value)} sx={{ width: 120 }}
                          inputProps={{ style: { fontSize: 13 } }} />
                        <Button size="small" variant="outlined" disabled={busy} onClick={() => sendAction('targetcalib', { value: targetPpm })}>Run Target Calibration</Button>
                      </Stack>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>ABC (Auto Baseline Correction)</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Automatically adjusts baseline over time. Set interval in hours (typically 24\u2013180h). Disable for controlled environments.
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <TextField size="small" label="Interval (hours)" type="number" value={abcHours}
                          onChange={(e) => setAbcHours(e.target.value)} sx={{ width: 130 }}
                          inputProps={{ style: { fontSize: 13 } }} />
                        <Button size="small" variant="outlined" disabled={busy} onClick={() => sendAction('ABCon', { hours: abcHours })}>Enable ABC</Button>
                        <Button size="small" variant="outlined" color="error" disabled={busy} onClick={() => sendAction('ABCoff')}>Disable ABC</Button>
                      </Stack>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>Factory Calibration Reset</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Reset calibration to factory defaults. Use if calibration has drifted or after a failed attempt.
                      </Typography>
                      <Button size="small" variant="outlined" color="error" disabled={busy}
                        onClick={() => { if (window.confirm('Reset calibration to factory defaults?')) sendAction('factory_reset_calib'); }}>
                        Reset to Factory
                      </Button>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>Status &amp; Diagnostics</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Request Status reads all registers. Run Diag tests whether register writes work (tests samples, IIR, meter control with both Modbus func 0x10 and 0x06).
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button size="small" variant="outlined" disabled={busy} onClick={() => sendAction('report_status')}>
                          Request Status
                        </Button>
                        <Button size="small" variant="outlined" color="error" disabled={busy} onClick={() => sendAction('set_continuous')}
                          title="Force sensor back to continuous mode — use if sensor is stuck in single mode (error 0x80)">
                          Force Continuous
                        </Button>
                        <Button size="small" variant="outlined" color="warning" disabled={busy} onClick={() => sendAction('diag_registers')}>
                          Write Diag
                        </Button>
                        <Button size="small" variant="outlined" color="warning" disabled={busy} onClick={() => sendAction('unlock_test')}>
                          Unlock Test
                        </Button>
                        <Button size="small" variant="outlined" color="warning" disabled={busy} onClick={() => sendAction('mode_test')}>
                          Mode Test
                        </Button>
                      </Stack>
                    </Box>
                  </Stack>
                  {actionMsg && (
                    <Typography variant="caption" color={actionMsg.startsWith('Queued') ? 'success.main' : 'error'} sx={{ display: 'block', mt: 2 }}>
                      {actionMsg}
                    </Typography>
                  )}
                </>
              ),
            },
          ].map(({ id, label, chips = [], content }) => (
            <Accordion key={id} expanded={expanded === id} onChange={(_, open) => setExpanded(open ? id : false)}
              sx={{ mb: 1, '&:before': { display: 'none' }, borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, mr: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{label}</Typography>
                  {chips}
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>{content}</AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>
    </>
  );
}

function InfoRow({ label, value }) {
  return (
    <Stack direction="row" spacing={1} sx={{ mb: 0.5 }} alignItems="center">
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 88, flexShrink: 0 }}>{label}</Typography>
      <Typography variant="caption" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>{value ?? '\u2014'}</Typography>
    </Stack>
  );
}
